import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * "81% Of Customers Also Purchased" block in the cart.
 * Adds the chosen variant to the cart and lets the theme re-render the cart (drawer or page).
 */
class MpCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.classList.contains('mp-upsell__select')) return;
    const option = select.selectedOptions[0];
    const item = select.closest('.mp-upsell__item');
    if (!option || !item) return;
    const price = item.querySelector('[data-mp-upsell-price]');
    const compare = item.querySelector('[data-mp-upsell-compare]');
    if (price) price.textContent = option.dataset.price || price.textContent;
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-mp-upsell-form')) return;
    event.preventDefault();

    const button = form.querySelector('.mp-upsell__add');
    const label = form.querySelector('.mp-upsell__add-text');
    const error = form.parentElement?.querySelector('.mp-upsell__error');
    if (button?.disabled) return;
    if (button) button.disabled = true;
    if (label) label.textContent = 'Adding…';
    if (error) error.hidden = true;

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (result.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'mp-cart-upsell', itemCount: 1 },
        });
        if (error) {
          error.textContent = result.description || result.message || 'Could not add this item.';
          error.hidden = false;
        }
        if (button) button.disabled = false;
        if (label) label.textContent = 'Add';
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'mp-cart-upsell',
          itemCount: 1,
          productId: result.product_id,
          sections: result.sections,
        },
      });
    } catch (err) {
      deferred.reject(err);
      if (error) {
        error.textContent = 'Could not add this item. Please try again.';
        error.hidden = false;
      }
      if (button) button.disabled = false;
      if (label) label.textContent = 'Add';
    }
  };
}

if (!customElements.get('mp-cart-upsell')) {
  customElements.define('mp-cart-upsell', MpCartUpsell);
}
